<template>
  <div class="global-container">
    <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
      <template v-slot:header-options>
        <div class="filters">
          <button v-for="mode in PAYMENT_MODES" :key="mode.value" class="filter-button" :class="{ active: isFilterActive('paymentType', mode.value) }"
                  :title="'Afficher uniquement les encaissements par ' + mode.label.toLowerCase()" @click="toggleFilter('paymentType', mode.value)">
            <span :class="['fas', mode.icon]"></span>{{ mode.label }}
          </button>
          <span class="filters-separator"></span>
          <button v-for="purpose in PURPOSES" :key="purpose.value" class="filter-button" :class="{ active: isFilterActive('paymentPurpose', purpose.value) }"
                  :title="'Afficher uniquement les encaissements de motif ' + purpose.label.toLowerCase()" @click="toggleFilter('paymentPurpose', purpose.value)">
            {{ purpose.label }}
          </button>
          <span class="filters-separator"></span>
          <button class="filter-button" :class="{ active: isDirectionActive(GREATER_THAN) }" title="Afficher uniquement les encaissements (montants positifs)" @click="toggleDirection(GREATER_THAN)">
            <span class="fas fa-arrow-down"></span>Entrées
          </button>
          <button class="filter-button" :class="{ active: isDirectionActive(MINUS_THAN) }" title="Afficher uniquement les décaissements (remboursements)" @click="toggleDirection(MINUS_THAN)">
            <span class="fas fa-arrow-up"></span>Sorties
          </button>
        </div>
        <button @click="exportToExcel" class="action-button ghost only-desktop-flex" type="button" title="Exporter les encaissements affichés (Excel)"><span class="fas fa-download"></span>Exporter</button>
      </template>
      <template v-slot:table>
        <thead>
        <tr>
          <custom-header class="col-fit" field="date">Date</custom-header>
          <custom-header class="col-fit" field="documentRef" :sortable="false">Pièce</custom-header>
          <custom-header field="clientName" :sortable="false">Client</custom-header>
          <custom-header class="col-fit" field="paymentPurpose">Motif</custom-header>
          <custom-header class="col-fit" field="paymentType">Mode</custom-header>
          <custom-header class="col-fit right" field="amount">Encaissement</custom-header>
          <custom-header class="col-fit right" field="amountOut" :sortable="false">Décaissement</custom-header>
        </tr>
        </thead>
        <tbody v-if="data && data.length > 0">
        <tr v-if="resumeData" class="total compact-row">
          <td>Total ({{ resumeData.count }})</td>
          <td colspan="4">Solde : {{ money(resumeData.ttcTotal) }}</td>
          <td class="right">{{ money(resumeData.inTotal) }}</td>
          <td class="right">{{ money(resumeData.outTotal) }}</td>
        </tr>
        <tr v-for="(collection, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" class="compact-row" :class="{editable: !isAccountantModule}" :title="isAccountantModule ? undefined : 'Modifier l\'encaissement'" @click="openEditCollectionModal(collection)">
          <custom-body class="col-fit" field="date">{{ displayDate(dateToFec(collection.date)) }}</custom-body>
          <custom-body class="col-fit" field="documentRef"><span class="doc-chip">{{ collection.documentRef || '-' }}</span></custom-body>
          <custom-body field="clientName">{{ collection.clientName || '-' }}</custom-body>
          <custom-body class="col-fit" field="paymentPurpose"><PaymentPurposeBadge :purpose="collection.paymentPurpose" /></custom-body>
          <custom-body class="col-fit" field="paymentType">{{ convertPaymentType(collection.paymentType) }}</custom-body>
          <custom-body class="col-fit right" field="amount">{{ collection.amount >= 0 ? money(collection.amount) : '' }}</custom-body>
          <custom-body class="col-fit right" field="amountOut">{{ collection.amount < 0 ? money(-collection.amount) : '' }}</custom-body>
        </tr>
        </tbody>
        <tbody v-else><tr class="empty-row"><td colspan="7">Aucun encaissement enregistré</td></tr></tbody>
      </template>
    </CustomTable>
    <EditCollectionModal ref="editCollectionModal" :callback="reload" />
  </div>
</template>

<script>
import CustomTable from "@/components/CustomTable.vue";
import {library} from '@fortawesome/fontawesome-svg-core';
import {faArrowDown, faArrowUp, faBuildingColumns, faCoins, faDownload, faMoneyCheck} from "@fortawesome/free-solid-svg-icons";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import PaymentPurposeBadge from "@/accounting/components/tables/commons/PaymentPurposeBadge.vue";
import EditCollectionModal from "@/components/modals/EditCollectionModal.vue";
import {AccountingController} from "@/accounting/controllers/AccountingController";
import {convertPaymentType} from "@/utils";
import {AccountingTableMixin} from "@/accounting/services/accountingTableMixin";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {PAYMENT_PURPOSE} from "@/models/paymentPurpose";
import {AccountingService} from "@/accounting/services/AccountingService";
import {dateToFec, displayDate, excelDate, money} from "@/accounting/services/export/AccountingFormat";
import {DATE_FORMAT, MONEY_FORMAT} from "@/accounting/services/export/ExcelTable";
library.add(faArrowDown, faArrowUp, faBuildingColumns, faCoins, faDownload, faMoneyCheck);

const PAYMENT_MODES = [
  {value: 'TRANSFER', label: convertPaymentType('TRANSFER'), icon: 'fa-building-columns'},
  {value: 'CHECK', label: convertPaymentType('CHECK'), icon: 'fa-money-check'},
  {value: 'CASH', label: convertPaymentType('CASH'), icon: 'fa-coins'},
];
const PURPOSES = [PAYMENT_PURPOSE.INVOICE, PAYMENT_PURPOSE.DEPOSIT, PAYMENT_PURPOSE.CREDIT_NOTE];
const DATE_FIELD = "date";
// Le back ignore un filtre dont la valeur est 0 (valeur « vide » dans le modèle) : le sens passe par un seuil d'un millième d'euro
const DIRECTION_THRESHOLD = 0.001;

/**
 * Encaissements de la période (et décaissements : remboursements d'avoirs), avec leur pièce, leur client et leur motif.
 * Filtres rapides : mode de paiement, motif, sens (entrées / sorties) ; chacun peut être retiré en recliquant dessus.
 */
export default {
  name: 'CollectionForAccountingTable',
  mixins: [AccountingTableMixin],
  components: {
    EditCollectionModal,
    PaymentPurposeBadge,
    CustomBody,
    CustomHeader,
    CustomTable
  },
  data: function () {
    return {
      PAYMENT_MODES,
      PURPOSES,
      dateField: DATE_FIELD,
      GREATER_THAN: FILTER_OPERATOR_ENUM.GREATER_THAN.value,
      MINUS_THAN: FILTER_OPERATOR_ENUM.MINUS_THAN.value,
      data: [],
      resumeData: undefined,
      elements: [
        {label: "Date", value: "date", type: "date"},
        {label: "Motif", value: "paymentPurpose", type: "text"},
        {label: "Mode", value: "paymentType", type: "text"},
        {label: "Montant", value: "amount", type: "number"},
      ],
      searchOptions: {
        filters: AccountingService.periodFilters(DATE_FIELD),
        sorts: []
      },
    }
  },
  methods: {
    convertPaymentType,
    displayDate,
    dateToFec,
    money,
    // Le module comptable consulte les encaissements des professionnels : pas de modification
    openEditCollectionModal(collection) {
      if (this.isAccountantModule) return;
      this.$refs.editCollectionModal.openModal(collection);
    },
    requestTable(urlParams, professionalId) {
      return AccountingController.getCollectionTable(urlParams, professionalId);
    },
    requestResume(urlParams, professionalId) {
      return AccountingController.getCollectionResume(urlParams, professionalId);
    },
    // Sens : entrées (montant > 0) ou sorties (montant < 0), un seul à la fois
    isDirectionActive(operator) {
      return this.currentFilters().some(filter => filter.field === "amount" && filter.operator === operator && Math.abs(filter.variable) === DIRECTION_THRESHOLD);
    },
    async toggleDirection(operator) {
      const wasActive = this.isDirectionActive(operator);
      const filters = this.currentFilters();
      for (let index = filters.length - 1; index >= 0; index--) {
        if (filters[index].field === "amount" && Math.abs(filters[index].variable) === DIRECTION_THRESHOLD) filters.splice(index, 1);
      }
      if (!wasActive) filters.push({field: "amount", operator, variable: operator === this.MINUS_THAN ? -DIRECTION_THRESHOLD : DIRECTION_THRESHOLD});
      await this.reload();
    },
    exportToExcel() {
      return this.downloadExcel('Encaissements', [
        {header: 'Date', pick: row => excelDate(dateToFec(row.date)), format: DATE_FORMAT},
        {header: 'Pièce', pick: row => row.documentRef},
        {header: 'Client', pick: row => row.clientName},
        {header: 'Motif', pick: row => (PAYMENT_PURPOSE[row.paymentPurpose] ?? PAYMENT_PURPOSE.INVOICE).label},
        {header: 'Mode', pick: row => convertPaymentType(row.paymentType)},
        {header: 'Encaissement', pick: row => row.amount > 0 ? row.amount : undefined, format: MONEY_FORMAT, total: true},
        {header: 'Décaissement', pick: row => row.amount < 0 ? -row.amount : undefined, format: MONEY_FORMAT, total: true},
      ]);
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';
@require '~/src/accounting/style/accountingTable.styl';
</style>
